import api from '../api';
import ProductStorageHelpers from '../../utils/product-local-storage-wrapper';

export interface CheckoutStockItem { id: number; quantity: number }
export interface CheckoutDeliveryResult {
  canProceed: boolean;
  usesPosStock: boolean;
  message: string;
  unavailableItems: Array<{ productId: number; productName: string; reason: string }>;
}
const invalidCart = 'Your cart changed or is empty. Go back to the cart, review the items and continue again.';
const signature = (items: CheckoutStockItem[]) => {
  if (!Array.isArray(items) || !items.length || items.length > 1000 || items.some(i =>
    !i || !Number.isSafeInteger(i.id) || i.id <= 0 || !Number.isFinite(i.quantity) || i.quantity <= 0)) throw new Error(invalidCart);
  return JSON.stringify(items.map(i => [i.id, i.quantity]).sort((a, b) => a[0] - b[0] || a[1] - b[1]));
};

// Validate exactly the checkout snapshot used for order totals. Never silently replace
// its items with another tab's cart without recalculating those totals through the cart step.
export function checkoutStockSnapshot() {
  const items: CheckoutStockItem[] | undefined = ProductStorageHelpers.GetProcessedProductStorage()?.items;
  const current = ProductStorageHelpers.GetProductStorage();
  if (!items || signature(items) !== signature(current)) throw new Error(invalidCart);
  return { items: items.map(i => ({ id: i.id, quantity: i.quantity })),
    key: JSON.stringify([ProductStorageHelpers.GetUserId(), signature(items)]) };
}

export async function checkCheckoutDelivery(userAddressId: number, items: CheckoutStockItem[], userId?: number): Promise<CheckoutDeliveryResult> {
  if (!Number.isSafeInteger(userAddressId) || userAddressId <= 0 ||
    (userId !== undefined && (!Number.isSafeInteger(userId) || userId <= 0))) throw new Error('Select a valid delivery address and customer.');
  signature(items);
  let data: CheckoutDeliveryResult;
  try {
    ({ data } = await api.post<CheckoutDeliveryResult>('/api/Order/CheckDeliveryAvailability', {
      userAddressId, ...(userId === undefined ? {} : { userId }),
      items: items.map(i => ({ productId: i.id, quantity: i.quantity }))
    }));
  } catch { throw new Error('Could not check stock for this delivery address. Please retry before continuing.'); }
  if (!data || typeof data.canProceed !== 'boolean' || typeof data.usesPosStock !== 'boolean' || typeof data.message !== 'string' ||
    !Array.isArray(data.unavailableItems) || (data.canProceed && data.unavailableItems.length > 0))
    throw new Error('Delivery stock could not be verified. Please retry.');
  if (!data.canProceed) throw new Error(data.message || 'Some items are unavailable. Change the cart or choose a different address.');
  return data;
}

export async function checkCurrentCheckoutDelivery(userAddressId: number, userId?: number): Promise<void> {
  const before = checkoutStockSnapshot();
  await checkCheckoutDelivery(userAddressId, before.items, userId);
  if (before.key !== checkoutStockSnapshot().key) throw new Error(invalidCart);
}
